import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Globe,
  Mail,
  Search,
  TestTube,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  Eye,
  BarChart3,
  TrendingUp,
  FileText,
  ChevronDown,
  ChevronUp,
  ListTodo,
  Clock
} from "lucide-react";

export default function AdminClients() {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [domains, setDomains] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clientReports, setClientReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("clients");
  const [expandedClients, setExpandedClients] = useState({});
  
  // Dialog states
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [viewClientDialogOpen, setViewClientDialogOpen] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [clientDetailReport, setClientDetailReport] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  
  // Form states
  const [clientForm, setClientForm] = useState({
    name: "",
    company_name: "",
    contact_email: "",
    notes: ""
  });
  
  const [domainForm, setDomainForm] = useState({
    client_id: "",
    domain: "",
    from_email: "",
    from_name: "GigsEasy",
    sparkpost_api_key: "",
    daily_limit: 10000,
    notes: "",
    tracking_mode: "internal",
    custom_tracking_domain: ""
  });
  
  const [testEmail, setTestEmail] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, domainsRes, tasksRes, reportsRes] = await Promise.all([
        axios.get(`${API}/v2/clients`, { headers }),
        axios.get(`${API}/v2/email-domains`, { headers }),
        axios.get(`${API}/v2/tasks`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/v2/reports/clients`, { headers }).catch(() => ({ data: [] }))
      ]);
      setClients(clientsRes.data?.items || clientsRes.data || []);
      setDomains(domainsRes.data?.items || domainsRes.data || []);
      setTasks(tasksRes.data?.items || tasksRes.data || []);
      setClientReports(reportsRes.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      await axios.post(`${API}/v2/clients`, clientForm, { headers });
      toast.success("Client created successfully");
      setClientDialogOpen(false);
      setClientForm({ name: "", company_name: "", contact_email: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    }
  };

  const handleUpdateClient = async () => {
    try {
      await axios.patch(`${API}/v2/clients/${selectedClient.client_id}`, clientForm, { headers });
      toast.success("Client updated successfully");
      setClientDialogOpen(false);
      setSelectedClient(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update client");
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!confirm("Delete this client and all its domains?")) return;
    try {
      await axios.delete(`${API}/v2/clients/${clientId}`, { headers });
      toast.success("Client deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const handleCreateDomain = async () => {
    try {
      await axios.post(`${API}/v2/email-domains`, domainForm, { headers });
      toast.success("Domain created successfully");
      setDomainDialogOpen(false);
      setDomainForm({
        client_id: "",
        domain: "",
        from_email: "",
        from_name: "GigsEasy",
        sparkpost_api_key: "",
        daily_limit: 10000,
        notes: "",
        tracking_mode: "internal",
        custom_tracking_domain: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create domain");
    }
  };

  const handleUpdateDomain = async () => {
    try {
      const updateData = { ...domainForm };
      delete updateData.client_id;
      delete updateData.domain;
      if (!updateData.sparkpost_api_key) delete updateData.sparkpost_api_key;
      
      await axios.patch(`${API}/v2/email-domains/${selectedDomain.domain_id}`, updateData, { headers });
      toast.success("Domain updated successfully");
      setDomainDialogOpen(false);
      setSelectedDomain(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update domain");
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!confirm("Delete this domain?")) return;
    try {
      await axios.delete(`${API}/v2/email-domains/${domainId}`, { headers });
      toast.success("Domain deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete domain");
    }
  };

  const handleTestDomain = async () => {
    try {
      const response = await axios.post(
        `${API}/v2/email-domains/${selectedDomain.domain_id}/test`,
        { test_email: testEmail },
        { headers }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setTestEmailDialogOpen(false);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to send test email");
    }
  };

  const handleToggleDomainStatus = async (domain) => {
    try {
      await axios.patch(
        `${API}/v2/email-domains/${domain.domain_id}`,
        { is_active: !domain.is_active },
        { headers }
      );
      toast.success(`Domain ${domain.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update domain status");
    }
  };

  const handleVerifyTrackingDomain = async (domainId) => {
    try {
      const response = await axios.post(
        `${API}/v2/email-domains/${domainId}/verify-tracking`,
        {},
        { headers }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify tracking domain");
    }
  };

  const openEditClient = (client) => {
    setSelectedClient(client);
    setClientForm({
      name: client.name,
      company_name: client.company_name || "",
      contact_email: client.contact_email,
      notes: client.notes || ""
    });
    setClientDialogOpen(true);
  };

  const openAddDomain = (client) => {
    setDomainForm({
      ...domainForm,
      client_id: client.client_id
    });
    setDomainDialogOpen(true);
  };

  const openEditDomain = (domain) => {
    setSelectedDomain(domain);
    setDomainForm({
      client_id: domain.client_id,
      domain: domain.domain,
      from_email: domain.from_email,
      from_name: domain.from_name || "GigsEasy",
      sparkpost_api_key: "",
      daily_limit: domain.daily_limit,
      notes: domain.notes || "",
      tracking_mode: domain.tracking_mode || "internal",
      custom_tracking_domain: domain.custom_tracking_domain || ""
    });
    setDomainDialogOpen(true);
  };

  const viewClientReport = async (clientId) => {
    try {
      const res = await axios.get(`${API}/v2/reports/client/${clientId}`, { headers });
      setClientDetailReport(res.data);
      setReportDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load client report");
    }
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.contact_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate totals for reports
  const totalEmailsSent = clientReports.reduce((sum, r) => sum + (r.total_emails_sent || 0), 0);
  const totalSubmissions = clientReports.reduce((sum, r) => sum + (r.total_submissions || 0), 0);
  const totalApproved = clientReports.reduce((sum, r) => sum + (r.approved_submissions || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="clients" onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="clients" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Clients & Domains
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-6 mt-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{clients.length}</p>
                <p className="text-sm text-slate-500">Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{domains.length}</p>
                <p className="text-sm text-slate-500">Domains</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{domains.filter(d => d.is_verified).length}</p>
                <p className="text-sm text-slate-500">Verified</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{domains.reduce((sum, d) => sum + (d.emails_sent_today || 0), 0)}</p>
                <p className="text-sm text-slate-500">Emails Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients Section */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Clients & Sending Domains
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-[200px]"
                />
              </div>
              <Dialog open={clientDialogOpen} onOpenChange={(open) => {
                setClientDialogOpen(open);
                if (!open) {
                  setSelectedClient(null);
                  setClientForm({ name: "", company_name: "", contact_email: "", notes: "" });
                }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Client
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{selectedClient ? "Edit Client" : "Add New Client"}</DialogTitle>
                    <DialogDescription>
                      {selectedClient ? "Update client information" : "Add a new client to send task emails from their domains"}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Client Name *</Label>
                      <Input
                        value={clientForm.name}
                        onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                        placeholder="e.g., Acme Corporation"
                      />
                    </div>
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        value={clientForm.company_name}
                        onChange={(e) => setClientForm({ ...clientForm, company_name: e.target.value })}
                        placeholder="Legal company name"
                      />
                    </div>
                    <div>
                      <Label>Contact Email *</Label>
                      <Input
                        type="email"
                        value={clientForm.contact_email}
                        onChange={(e) => setClientForm({ ...clientForm, contact_email: e.target.value })}
                        placeholder="contact@company.com"
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={clientForm.notes}
                        onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                        placeholder="Internal notes about this client..."
                        rows={3}
                      />
                    </div>
                    <Button 
                      onClick={selectedClient ? handleUpdateClient : handleCreateClient} 
                      className="w-full"
                    >
                      {selectedClient ? "Update Client" : "Create Client"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredClients.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No clients found</p>
              <p className="text-sm">Add a client to start managing sending domains</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredClients.map((client) => {
                const clientDomains = domains.filter(d => d.client_id === client.client_id);
                const clientTasks = tasks.filter(t => t.client_id === client.client_id);
                const isExpanded = expandedClients[client.client_id];
                
                return (
                  <div key={client.client_id} className="p-4" data-testid={`client-row-${client.client_id}`}>
                    {/* Client Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{client.name}</h3>
                            <Badge variant="outline" className={client.is_active ? "text-green-600 border-green-200" : "text-slate-400"}>
                              {client.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            {client.company_name || client.contact_email} · {clientDomains.length} domain{clientDomains.length !== 1 ? 's' : ''} · {clientTasks.length} task{clientTasks.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAddDomain(client)}
                          data-testid={`add-domain-btn-${client.client_id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Domain
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedClients(prev => ({ ...prev, [client.client_id]: !prev[client.client_id] }))}
                          title={isExpanded ? "Collapse" : "Expand Tasks"}
                          data-testid={`expand-btn-${client.client_id}`}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditClient(client)}
                          title="Edit Client"
                          data-testid={`edit-client-btn-${client.client_id}`}
                        >
                          <Pencil className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteClient(client.client_id)}
                          title="Delete Client"
                          data-testid={`delete-client-btn-${client.client_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    {/* Client Domains */}
                    {clientDomains.length > 0 && (
                      <div className="ml-13 mt-3 space-y-2">
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Sending Domains</p>
                        {clientDomains.map((domain) => (
                          <div 
                            key={domain.domain_id}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <Globe className="w-4 h-4 text-slate-400" />
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{domain.domain}</span>
                                  {domain.is_verified ? (
                                    <Badge className="bg-green-100 text-green-700 text-xs">Verified</Badge>
                                  ) : (
                                    <Badge className="bg-amber-100 text-amber-700 text-xs">Unverified</Badge>
                                  )}
                                  {!domain.is_active && (
                                    <Badge variant="outline" className="text-slate-400 text-xs">Disabled</Badge>
                                  )}
                                  {/* Tracking Mode Badge */}
                                  <Badge variant="outline" className="text-xs">
                                    {domain.tracking_mode === "esp" ? "ESP Tracking" : 
                                     domain.tracking_mode === "custom" ? "Custom Domain" : 
                                     "Internal Tracking"}
                                  </Badge>
                                </div>
                                <p className="text-xs text-slate-500">
                                  From: {domain.from_email} · Limit: {domain.daily_limit?.toLocaleString()}/day
                                  {domain.tracking_mode === "custom" && domain.custom_tracking_domain && (
                                    <span className="ml-2">
                                      · Track: {domain.custom_tracking_domain}
                                      {domain.tracking_domain_verified ? (
                                        <CheckCircle className="w-3 h-3 inline ml-1 text-green-500" />
                                      ) : (
                                        <AlertCircle className="w-3 h-3 inline ml-1 text-amber-500" />
                                      )}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedDomain(domain);
                                  setTestEmail("");
                                  setTestEmailDialogOpen(true);
                                }}
                                title="Test Domain"
                              >
                                <TestTube className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditDomain(domain)}
                                title="Edit Domain"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleDomainStatus(domain)}
                                title={domain.is_active ? "Disable" : "Enable"}
                              >
                                {domain.is_active ? (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteDomain(domain.domain_id)}
                                title="Delete Domain"
                                data-testid={`delete-domain-btn-${domain.domain_id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                              {/* Verify tracking domain button */}
                              {domain.tracking_mode === "custom" && domain.custom_tracking_domain && !domain.tracking_domain_verified && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleVerifyTrackingDomain(domain.domain_id)}
                                  title="Verify Tracking Domain"
                                  className="text-blue-600 border-blue-200"
                                >
                                  Verify
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Client Tasks - Expandable Section */}
                    {isExpanded && (
                      <div className="ml-13 mt-4 space-y-2">
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Associated Tasks</p>
                        {clientTasks.length === 0 ? (
                          <div className="p-3 bg-slate-50 rounded-lg text-center text-sm text-slate-500">
                            <ListTodo className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                            No tasks associated with this client
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {clientTasks.map((task) => (
                              <div 
                                key={task.task_id}
                                className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                                data-testid={`task-row-${task.task_id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <ListTodo className="w-4 h-4 text-blue-500" />
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">{task.title}</span>
                                      {task.is_active ? (
                                        <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-slate-400 text-xs">Inactive</Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                      {task.task_type?.replace('_', ' ')} · {task.points_reward} pts · {task.completion_method === 'auto' ? 'Auto-complete' : 'Manual approval'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <Clock className="w-3 h-3" />
                                  {task.completion_minutes || 10} min
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Domain Dialog */}
      <Dialog open={domainDialogOpen} onOpenChange={(open) => {
        setDomainDialogOpen(open);
        if (!open) {
          setSelectedDomain(null);
          setDomainForm({
            client_id: "",
            domain: "",
            from_email: "",
            from_name: "GigsEasy",
            sparkpost_api_key: "",
            daily_limit: 10000,
            notes: ""
          });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedDomain ? "Edit Domain" : "Add Sending Domain"}</DialogTitle>
            <DialogDescription>
              {selectedDomain 
                ? "Update domain settings and SparkPost configuration" 
                : "Configure a new sending domain with SparkPost"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {!selectedDomain && (
              <div>
                <Label>Domain *</Label>
                <Input
                  value={domainForm.domain}
                  onChange={(e) => setDomainForm({ ...domainForm, domain: e.target.value })}
                  placeholder="e.g., mail.company.com"
                />
              </div>
            )}
            <div>
              <Label>From Email *</Label>
              <Input
                value={domainForm.from_email}
                onChange={(e) => setDomainForm({ ...domainForm, from_email: e.target.value })}
                placeholder="e.g., tasks@company.com"
              />
            </div>
            <div>
              <Label>From Name</Label>
              <Input
                value={domainForm.from_name}
                onChange={(e) => setDomainForm({ ...domainForm, from_name: e.target.value })}
                placeholder="e.g., Company Tasks"
              />
            </div>
            <div>
              <Label>SparkPost API Key *</Label>
              <Input
                type="password"
                value={domainForm.sparkpost_api_key}
                onChange={(e) => setDomainForm({ ...domainForm, sparkpost_api_key: e.target.value })}
                placeholder={selectedDomain ? "Enter new key to change..." : "Your SparkPost API key"}
              />
              {selectedDomain && (
                <p className="text-xs text-slate-500 mt-1">
                  Current key: {selectedDomain.sparkpost_api_key_masked || "****"}
                </p>
              )}
            </div>
            <div>
              <Label>Daily Send Limit</Label>
              <Input
                type="number"
                value={domainForm.daily_limit}
                onChange={(e) => setDomainForm({ ...domainForm, daily_limit: parseInt(e.target.value) || 10000 })}
              />
            </div>
            
            {/* Tracking Mode Configuration */}
            <div className="pt-4 border-t">
              <Label className="text-sm font-semibold">Click & Open Tracking</Label>
              <p className="text-xs text-slate-500 mb-3">Choose how to track email clicks and opens</p>
              <Select
                value={domainForm.tracking_mode}
                onValueChange={(value) => setDomainForm({ ...domainForm, tracking_mode: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tracking mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">
                    <div className="flex flex-col">
                      <span className="font-medium">Internal Tracking</span>
                      <span className="text-xs text-slate-500">Use GigsEasy server for tracking URLs</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="esp">
                    <div className="flex flex-col">
                      <span className="font-medium">ESP Tracking (SparkPost)</span>
                      <span className="text-xs text-slate-500">Use SparkPost's built-in tracking via webhooks</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex flex-col">
                      <span className="font-medium">Custom Domain Tracking</span>
                      <span className="text-xs text-slate-500">Use your own tracking subdomain</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {domainForm.tracking_mode === "custom" && (
              <div>
                <Label>Custom Tracking Domain</Label>
                <Input
                  value={domainForm.custom_tracking_domain}
                  onChange={(e) => setDomainForm({ ...domainForm, custom_tracking_domain: e.target.value })}
                  placeholder="track.yourdomain.com"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Add a CNAME record pointing this subdomain to GigsEasy's server
                </p>
              </div>
            )}
            
            {domainForm.tracking_mode === "esp" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                <strong>Note:</strong> ESP tracking requires a SparkPost webhook configured to receive click events. 
                If this domain shares a SparkPost account with marketing emails, you may receive unrelated events.
              </div>
            )}
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={domainForm.notes}
                onChange={(e) => setDomainForm({ ...domainForm, notes: e.target.value })}
                placeholder="Internal notes..."
                rows={2}
              />
            </div>
            <Button 
              onClick={selectedDomain ? handleUpdateDomain : handleCreateDomain} 
              className="w-full"
            >
              {selectedDomain ? "Update Domain" : "Add Domain"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Domain: {selectedDomain?.domain}</DialogTitle>
            <DialogDescription>
              Send a test email to verify SparkPost integration is working
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Send Test Email To</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p>This will send a test email from:</p>
              <p className="font-medium mt-1">{selectedDomain?.from_email}</p>
            </div>
            <Button onClick={handleTestDomain} className="w-full">
              <Send className="w-4 h-4 mr-2" />
              Send Test Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Detail Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              {clientDetailReport?.client?.name} - Performance Report
            </DialogTitle>
          </DialogHeader>
          {clientDetailReport && (
            <div className="space-y-6 pt-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{clientDetailReport.summary?.total_domains}</p>
                  <p className="text-sm text-blue-700">Domains</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{clientDetailReport.summary?.total_tasks}</p>
                  <p className="text-sm text-purple-700">Tasks</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{clientDetailReport.summary?.total_approved}</p>
                  <p className="text-sm text-green-700">Completed</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{clientDetailReport.summary?.total_submissions}</p>
                  <p className="text-sm text-amber-700">Total Submissions</p>
                </div>
              </div>

              {/* Domains */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Domains ({clientDetailReport.domains?.length || 0})
                </h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>From Email</TableHead>
                      <TableHead>Emails Today</TableHead>
                      <TableHead>Total Sent</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientDetailReport.domains?.map(d => (
                      <TableRow key={d.domain_id}>
                        <TableCell className="font-medium">{d.domain}</TableCell>
                        <TableCell>{d.from_email}</TableCell>
                        <TableCell>{d.emails_sent_today || 0}</TableCell>
                        <TableCell>{d.total_emails_sent || 0}</TableCell>
                        <TableCell>
                          <Badge className={d.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                            {d.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Tasks */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Tasks ({clientDetailReport.tasks?.length || 0})
                </h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>Submissions</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientDetailReport.tasks?.map(t => (
                      <TableRow key={t.task_id}>
                        <TableCell className="font-medium">{t.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{t.task_type?.replace("_", " ")}</Badge>
                        </TableCell>
                        <TableCell className="text-amber-600">{t.points_reward} pts</TableCell>
                        <TableCell>{t.total_submissions}</TableCell>
                        <TableCell className="text-green-600">{t.approved}</TableCell>
                        <TableCell>
                          <span className={t.completion_rate >= 50 ? "text-green-600" : "text-amber-600"}>
                            {t.completion_rate}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6 mt-6">
          {/* Reports Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{totalEmailsSent.toLocaleString()}</p>
                    <p className="text-sm text-slate-500">Total Emails Sent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{totalSubmissions.toLocaleString()}</p>
                    <p className="text-sm text-slate-500">Total Submissions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{totalApproved.toLocaleString()}</p>
                    <p className="text-sm text-slate-500">Tasks Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {totalSubmissions > 0 ? Math.round((totalApproved / totalSubmissions) * 100) : 0}%
                    </p>
                    <p className="text-sm text-slate-500">Completion Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Client Performance Table */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Client Performance
              </CardTitle>
              <CardDescription>Overview of all clients and their task completion metrics</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Domains</TableHead>
                    <TableHead>Tasks</TableHead>
                    <TableHead>Emails Sent</TableHead>
                    <TableHead>Submissions</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientReports.map(report => (
                    <TableRow key={report.client_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{report.name}</p>
                          <p className="text-sm text-slate-500">{report.company_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>{report.domains_count}</TableCell>
                      <TableCell>{report.tasks_count}</TableCell>
                      <TableCell>{report.total_emails_sent?.toLocaleString()}</TableCell>
                      <TableCell>{report.total_submissions}</TableCell>
                      <TableCell className="text-green-600 font-medium">{report.approved_submissions}</TableCell>
                      <TableCell>
                        <span className={report.completion_rate >= 50 ? "text-green-600 font-medium" : "text-amber-600 font-medium"}>
                          {report.completion_rate}%
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewClientReport(report.client_id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
